import { ShieldCheck, AlertTriangle, ShieldAlert, Info } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const ZONE_STYLE = {
  safe: {
    bg: "bg-green-50",
    border: "border-green-200",
    text: "text-green-700",
    score: "text-green-800",
    Icon: ShieldCheck,
  },
  grey: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-800",
    score: "text-amber-900",
    Icon: AlertTriangle,
  },
  distress: {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    score: "text-red-800",
    Icon: ShieldAlert,
  },
  na: {
    bg: "bg-slate-50",
    border: "border-slate-200",
    text: "text-slate-600",
    score: "text-slate-800",
    Icon: Info,
  },
};

const COMPONENT_LABELS = {
  x1_working_capital_ratio: { it: "X1 · Capitale circolante / Attivo", en: "X1 · Working capital / Assets" },
  x2_retained_earnings_ratio: { it: "X2 · Riserve / Attivo", en: "X2 · Retained earnings / Assets" },
  x3_ebit_ratio: { it: "X3 · EBIT / Attivo", en: "X3 · EBIT / Assets" },
  x4_equity_to_debt: { it: "X4 · Patrimonio netto / Debiti", en: "X4 · Equity / Debt" },
  x5_asset_turnover: { it: "X5 · Ricavi / Attivo", en: "X5 · Sales / Assets" },
};

export default function AltmanCard({ altman }) {
  const { t, lang } = useLang();
  if (!altman) return null;
  const zone = altman.zone || "na";
  const st = ZONE_STYLE[zone] || ZONE_STYLE.na;
  const { Icon } = st;

  const zoneLabelKey = `altman_zone_${zone}`;
  const zoneDescKey = `altman_desc_${zone}`;

  return (
    <div className={`overflow-hidden rounded-xl border ${st.border} ${st.bg}`} data-testid="altman-card">
      <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex items-start gap-4">
          <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white ${st.text}`}>
            <Icon className="h-6 w-6" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
              {t("altman_title")}
            </div>
            <div className="mt-2 flex items-end gap-3">
              <div className={`font-mono text-4xl font-medium ${st.score}`}>
                {altman.score !== null && altman.score !== undefined ? altman.score : "—"}
              </div>
              <div className={`mb-1 rounded-full border ${st.border} bg-white px-3 py-1 text-xs font-medium ${st.text}`}>
                {t(zoneLabelKey)}
              </div>
            </div>
            <p className={`mt-3 max-w-md text-sm ${st.text}`}>{t(zoneDescKey)}</p>
          </div>
        </div>
        <div>
          <div className="mb-3 text-xs font-medium uppercase tracking-widest text-slate-500">
            {t("altman_components")}
          </div>
          <div className="space-y-2 rounded-lg bg-white p-4 ring-1 ring-slate-200">
            {Object.entries(altman.components || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {COMPONENT_LABELS[k]?.[lang] || k}
                </span>
                <span className="font-mono font-medium text-slate-900">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-500">
            Z' = 0.717·X1 + 0.847·X2 + 3.107·X3 + 0.420·X4 + 0.998·X5
          </div>
        </div>
      </div>
    </div>
  );
}
